import { firstValueFrom, map } from 'rxjs';

import { buildDropRequest, isZoneLegit } from './drag-tree.utils';
import { MutableTreeManager } from './mutable-tree-manager';
import { SimpleTreeManager } from './simple-tree-manager';
import { TreeManager } from './tree-core/tree-manager';
import { DropRequest, DropUIZone, TreeUIFacade } from './tree-core/tree-ui-facade';
import { Node } from './tree-core/tree-view';
import { FlatNode, TreeViewModel } from './tree-core/tree-view-model';
import { WorkPackageTreeNode } from './wp-tree-view.component';
import { BaseWorkPackageDto } from '@api/backend-connector/models';
import { UIDropRequest, WorkPackagesService } from 'src/app/services/work-package.service';

export class WorkPackageTreeUiFacade implements TreeUIFacade<WorkPackageTreeNode> {
  private readonly treeManager: TreeManager<WorkPackageTreeNode>;

  private readonly treeViewModel: TreeViewModel<WorkPackageTreeNode>;

  constructor(
    private readonly projectId: string,
    private readonly workPackagesService: WorkPackagesService,
    private readonly autoSaveMoves: boolean,
  ) {
    const simpleTreeManager = new SimpleTreeManager<WorkPackageTreeNode>();

    this.treeManager = this.autoSaveMoves
      ? simpleTreeManager
      : new MutableTreeManager<WorkPackageTreeNode>(simpleTreeManager);

    this.treeViewModel = new TreeViewModel<WorkPackageTreeNode>(this.treeManager);
  }

  getFlattenNodes() {
    return this.treeViewModel.flattenNodes();
  }

  async nodeExpand(node: FlatNode<WorkPackageTreeNode>) {
    const simpleNode = this.treeManager.getNode(node.key);
    if (!simpleNode.children.length) {
      await this.fetchNodes(simpleNode);
    }
    this.treeViewModel.expandNode(node.key);
  }

  nodeCollapse(node: FlatNode<WorkPackageTreeNode>) {
    this.treeViewModel.collapseNode(node.key);
  }

  async initNodes() {
    return this.fetchNodes();
  }

  private async fetchNodes(parent?: Node<WorkPackageTreeNode>) {
    const items = await firstValueFrom(
      this.expandWorkPackage(this.projectId, parent?.data.id, parent?.data.childrenCount),
    );

    for (const item of items) {
      this.treeManager.addNode(item.id, item, parent?.key, item.position);
    }
  }

  private expandWorkPackage(projectId: string, parentId: string | undefined, childrenCount?: number) {
    if (parentId) {
      return this.workPackagesService
        .listWorkPackages({
          sorting: ['position'],
          projectId,
          parentId,
          limit: childrenCount,
        })
        .pipe(map(({ items }) => items));
    }
    return this.workPackagesService.listRootWorkPackages(projectId, 1000);
  }

  async updateNodeHandler(id: string) {
    const node = this.treeManager.tryGetNode(id);
    if (!node) {
      return false;
    }
    const wp = await firstValueFrom(this.workPackagesService.getWorkPackage({ id }));
    this.treeManager.updateNode(id, wp);
    return true;
  }

  async createNodeHandler(wp: BaseWorkPackageDto) {
    if (this.projectId !== wp.projectId) {
      return false;
    }

    // qui c'e' un bug. Devo aggiungere il nodo solo se il parent e' gia' presente nell'albero noto
    this.treeManager.addNode(wp.id, wp, wp.parentId ?? undefined, wp.position);
    return true;
  }

  async dragEndHandler(request: DropRequest, resetDrag: () => void) {
    // A. no autosave: apply changes only in memory
    if (!this.autoSaveMoves) {
      try {
        this.treeManager.moveNode(request.id, request.parentId, request.position);
      } catch (error) {
        console.error(error);
        // TODO toast error message
        // some are already managed (dropWorkPackage api call)
      } finally {
        resetDrag();
      }
      return true;
    }

    // B. autosave: call API and then apply changes in memory
    const afterDropped = () => {
      resetDrag();
    };
    await firstValueFrom(this.workPackagesService.dropWorkPackage({ ...request, afterDropped }));

    return false;
  }

  droppedNodeHandler(request: UIDropRequest) {
    // Important:
    // I don't remember the reason for this correction, which used to be before the API Call.
    // Having preprocessing before making the API call complicates the wp parent update operation.
    // Furthermore, it doesn't completely solve the problem because if some children have already been loaded,
    // fetchNodes would currently fail with its addNode, because it would already find the elements in the map.
    // Commenting out to explicitly keep this tracked
    // if (parentNode && parentNode.children.length < parentNode.data.childrenCount) {
    //   await this.fetchNodes(parentNode);
    // }

    const parentNode = request.parentId ? this.treeManager.getNode(request.parentId) : undefined;
    if (parentNode) {
      this.treeManager.updateNode(parentNode.key, {
        ...parentNode.data,
        childrenCount: parentNode.data.childrenCount + 1,
      });
    }

    this.treeManager.moveNode(request.id, request.parentId, request.position);
  }

  nodeCollapseHandler(key: string) {
    this.treeViewModel.collapseNode(key);
  }

  isDragZoneLegit(draggingKey: string, key: string) {
    return isZoneLegit(this.treeManager, draggingKey, key);
  }

  buildDropRequest(draggingKey: string | undefined, dropZone: DropUIZone | undefined) {
    return buildDropRequest(this.treeManager, draggingKey, dropZone);
  }
}
